import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import api from '@/services/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  FolderIcon,
  UsersIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

const countries = [
  'Australia', 'Austria', 'Belgium', 'Brazil', 'Canada', 'Denmark', 'Finland',
  'France', 'Germany', 'India', 'Ireland', 'Italy', 'Japan', 'Mexico',
  'Netherlands', 'New Zealand', 'Norway', 'Portugal', 'Singapore', 'South Korea',
  'Spain', 'Sweden', 'Switzerland', 'United Kingdom', 'United States',
].sort();

interface OrgMember {
  _id: string;
  name: string;
  email: string;
  role: string;
}

const getSteps = (hasOrg: boolean, hasProjects: boolean) => {
  if (hasOrg && hasProjects) return []; // No steps needed, will redirect
  if (hasOrg) return [
    { id: 1, label: 'Workspace', icon: BuildingOfficeIcon, description: 'Review your organization' },
    { id: 2, label: 'Create Project', icon: FolderIcon, description: 'Start your first project' },
    { id: 3, label: 'Invite Team', icon: UsersIcon, description: 'Add members (optional)' },
  ];
  return [
    { id: 1, label: 'Create Workspace', icon: BuildingOfficeIcon, description: 'Set up your organization' },
    { id: 2, label: 'Create Project', icon: FolderIcon, description: 'Start your first project' },
    { id: 3, label: 'Invite Team', icon: UsersIcon, description: 'Add members (optional)' },
  ];
};

const OnboardingPage: React.FC = () => {
  const { user, createOrganization, needsOnboarding, setActiveOrganization, activeOrganization } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCreateMode = searchParams.get('mode') === 'create';

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [hasExistingOrg, setHasExistingOrg] = useState(false);
  const [hasExistingProjects, setHasExistingProjects] = useState(false);

  // Check existing org and projects on mount (wait for user to be loaded)
  useEffect(() => {
    // Don't check until user data is available
    if (!user) return;

    const checkExistingSetup = async () => {
      // If user is explicitly creating a new organization, skip the checks
      if (isCreateMode) {
        setIsChecking(false);
        return;
      }

      try {
        // Check if user has organizations
        if (user.organizations && user.organizations.length > 0) {
          setHasExistingOrg(true);

          if (user.organizations.length > 1 && !activeOrganization) {
            // Multiple organizations and no active one selected - redirect to selection
            navigate('/select-organization', { replace: true });
            return;
          }

          // Single organization - set it as active
          if (!activeOrganization) {
            await setActiveOrganization(user.organizations[0]);
          }

          // Check if org has projects
          const response = await api.get('/projects?limit=1');
          const data = response.data as any;
          if (data.success && data.data?.projects?.length > 0) {
            setHasExistingProjects(true);
            // Both org and projects exist - go to dashboard
            navigate('/app', { replace: true });
            return;
          }
        }
      } catch (err) {
        // No projects or error, continue with onboarding
        console.log('No projects found or error checking projects:', err);
      } finally {
        setIsChecking(false);
      }
    };

    checkExistingSetup();
  }, [user, navigate, isCreateMode, setActiveOrganization, activeOrganization]);

  const STEPS = getSteps(hasExistingOrg, hasExistingProjects);

  // Step 1 — Org
  const [orgName, setOrgName] = useState('');
  const [orgCountry, setOrgCountry] = useState('');

  // Step 2 — Project
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const [projectDesc, setProjectDesc] = useState('');

  // Step 3 — Members
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [addedMembers, setAddedMembers] = useState<OrgMember[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedEmails, setInvitedEmails] = useState<string[]>([]);
  const [membersTab, setMembersTab] = useState<'existing' | 'invite'>('existing');

  // Load org members when reaching step 3 (only once)
  useEffect(() => {
    if (step === 3 && orgMembers.length === 0) {
      loadOrgMembers();
    }
  }, [step, orgMembers.length]);

  const loadOrgMembers = async () => {
    try {
      const response = await api.get('/users?limit=50');
      const data = response.data as any;
      if (data.success) {
        setOrgMembers(data.data?.users || []);
      }
    } catch {
      // No members yet is fine
      setOrgMembers([]); // Set empty array to prevent retry
    }
  };

  // Show loading while checking (must be after all hooks)
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking your setup...</p>
        </div>
      </div>
    );
  }

  // ── Step 1: Create Org ──────────────────────────────────────────────────
  const handleCreateOrg = async () => {
    if (!orgName.trim()) { toast.error('Organization name is required'); return; }
    if (!orgCountry) { toast.error('Please select a country'); return; }

    setIsLoading(true);
    try {
      await createOrganization(orgName.trim(), orgCountry);
      setStep(2);
    } catch {
      // error shown by createOrganization
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2: Create Project ──────────────────────────────────────────────
  const handleCreateProject = async () => {
    if (!projectName.trim()) { toast.error('Project name is required'); return; }

    setIsLoading(true);
    try {
      const response = await api.post('/projects', {
        name: projectName.trim(),
        description: projectDesc.trim() || undefined,
        status: 'active',
        priority: 'medium',
      });
      const data = response.data as any;
      if (data.success) {
        setProjectId(data.data._id);
        toast.success('Project created!');
        setStep(3);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 3: Add Member from existing list ──────────────────────────────
  const handleAddMember = async (member: OrgMember) => {
    if (!projectId) return;
    if (addedMembers.find(m => m._id === member._id)) return;

    try {
      await api.post(`/projects/${projectId}/members`, { userId: member._id });
      setAddedMembers(prev => [...prev, member]);
      toast.success(`${member.name} added to project`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to add member');
    }
  };

  // ── Step 3: Invite by email ─────────────────────────────────────────────
  const handleAddInviteEmail = () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (invitedEmails.includes(email)) {
      toast.error('Email already added');
      return;
    }
    setInvitedEmails(prev => [...prev, email]);
    setInviteEmail('');
  };

  const handleFinish = async () => {
    if (isLoading) return; // Prevent multiple calls
    
    setIsLoading(true);
    
    try {
      if (invitedEmails.length > 0) {
        toast.success(`Invitations will be sent to ${invitedEmails.length} email(s)`);
      }
      
      // Set active organization if user has organizations
      if (user?.organizations && user.organizations.length > 0) {
        await setActiveOrganization(user.organizations[0]);
      }
      
      navigate('/app');
    } catch (error) {
      console.error('Error in handleFinish:', error);
      toast.error('An error occurred during finish');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipSetup = async () => {
    if (isLoading) return; // Prevent multiple calls
    
    setIsLoading(true);
    
    try {
      toast.success('Setup skipped. You can complete it later from your dashboard.');
      
      // Set active organization if user has organizations
      if (user?.organizations && user.organizations.length > 0) {
        await setActiveOrganization(user.organizations[0]);
      }
      
      navigate('/app');
    } catch (error) {
      console.error('Error in handleSkipSetup:', error);
      toast.error('An error occurred during skip');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredMembers = orgMembers.filter(m =>
    m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.email.toLowerCase().includes(memberSearch.toLowerCase())
  );

  // ── UI ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
        <span className="text-xl font-bold text-blue-600 dark:text-blue-400">Prime AI</span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Welcome, <span className="font-medium text-gray-800 dark:text-white">{user?.name}</span>
        </span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          {/* Step progress */}
          <div className="flex items-center justify-center mb-10">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const isCompleted = step > s.id;
              const isCurrent = step === s.id;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300
                      ${isCompleted ? 'bg-green-500 text-white shadow-md' : ''}
                      ${isCurrent ? 'bg-blue-600 text-white shadow-lg ring-4 ring-blue-100 dark:ring-blue-900' : ''}
                      ${!isCurrent && !isCompleted ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500' : ''}
                    `}>
                      {isCompleted
                        ? <CheckCircleIcon className="w-5 h-5" />
                        : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`mt-2 text-xs font-medium ${isCurrent ? 'text-blue-600 dark:text-blue-400' : isCompleted ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                      {s.label}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-3 mb-5 transition-colors duration-300 ${step > s.id ? 'bg-green-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">

            {/* ── STEP 1: Create Organization OR Review Existing ── */}
            {step === 1 && (
              <div className="space-y-6">
                {hasExistingOrg ? (
                  <>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Your workspace is ready</h1>
                      <p className="mt-1 text-gray-500 dark:text-gray-400">Review your organization details and continue.</p>
                    </div>
                    
                    {/* Show existing org details */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                          <BuildingOfficeIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wide">Organization</p>
                          <p className="text-lg font-semibold text-gray-900 dark:text-white">{user?.organization?.name || 'Your Organization'}</p>
                        </div>
                      </div>
                      
                      <div className="border-t border-blue-200 dark:border-blue-800 pt-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Country</p>
                            <p className="font-medium text-gray-900 dark:text-white">{user?.organization?.country || 'Not set'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Role</p>
                            <p className="font-medium text-gray-900 dark:text-white capitalize">{user?.role || 'Owner'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => navigate('/app')}
                        className="px-5 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
                      >
                        Go to Dashboard
                      </button>
                      <button
                        onClick={() => setStep(2)}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                      >
                        Continue to Project <ArrowRightIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Set up your workspace</h1>
                      <p className="mt-1 text-gray-500 dark:text-gray-400">Create an organization to collaborate with your team.</p>
                    </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Organization Name *
                    </label>
                    <div className="relative">
                      <BuildingOfficeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={orgName}
                        onChange={e => setOrgName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCreateOrg()}
                        placeholder="e.g. Acme Corp"
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Country *
                    </label>
                    <div className="relative">
                      <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <select
                        value={orgCountry}
                        onChange={e => setOrgCountry(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                      >
                        <option value="">Select country</option>
                        {countries.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCreateOrg}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                >
                  {isLoading ? 'Creating...' : <>Continue <ArrowRightIcon className="w-4 h-4" /></>}
                </button>
                  </>
                )}
              </div>
            )}

            {/* ── STEP 2: Create Project ── */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create your first project</h1>
                  <p className="mt-1 text-gray-500 dark:text-gray-400">Projects help you organize your work and team.</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Project Name *
                    </label>
                    <div className="relative">
                      <FolderIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={projectName}
                        onChange={e => setProjectName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCreateProject()}
                        placeholder="e.g. Product Launch 2025"
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={projectDesc}
                      onChange={e => setProjectDesc(e.target.value)}
                      rows={3}
                      placeholder="What is this project about?"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  {!hasExistingOrg && (
                    <button
                      onClick={() => setStep(1)}
                      className="px-5 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={handleCreateProject}
                    disabled={isLoading}
                    className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                  >
                    {isLoading ? 'Creating...' : <>Continue <ArrowRightIcon className="w-4 h-4" /></>}
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 3: Invite Team ── */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invite your team</h1>
                  <p className="mt-1 text-gray-500 dark:text-gray-400">Add members to <span className="font-medium text-gray-700 dark:text-gray-200">"{projectName}"</span> or invite new users.</p>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setMembersTab('existing')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${membersTab === 'existing' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                  >
                    Add from organization
                  </button>
                  <button
                    onClick={() => setMembersTab('invite')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${membersTab === 'invite' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                  >
                    Invite by email
                  </button>
                </div>

                {/* Tab: Existing org members */}
                {membersTab === 'existing' && (
                  <div className="space-y-3">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={memberSearch}
                        onChange={e => setMemberSearch(e.target.value)}
                        placeholder="Search members..."
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {filteredMembers.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                        <UsersIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No other members in your organization yet.</p>
                        <p className="text-xs mt-1">Invite them by email in the next tab.</p>
                      </div>
                    ) : (
                      <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
                        {filteredMembers.map(member => {
                          const isAdded = addedMembers.some(m => m._id === member._id);
                          return (
                            <div key={member._id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-700 dark:text-blue-300 font-semibold text-sm">
                                  {member.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-800 dark:text-white">{member.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleAddMember(member)}
                                disabled={isAdded}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                  isAdded
                                    ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400 cursor-default'
                                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
                                }`}
                              >
                                {isAdded ? <><CheckCircleIcon className="w-3.5 h-3.5" /> Added</> : <><PlusIcon className="w-3.5 h-3.5" /> Add</>}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Tab: Invite by email */}
                {membersTab === 'invite' && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={e => setInviteEmail(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleAddInviteEmail()}
                          placeholder="colleague@company.com"
                          className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        onClick={handleAddInviteEmail}
                        className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                      >
                        <PlusIcon className="w-4 h-4" /> Add
                      </button>
                    </div>

                    {invitedEmails.length > 0 && (
                      <div className="space-y-1 max-h-44 overflow-y-auto">
                        {invitedEmails.map(email => (
                          <div key={email} className="flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <div className="flex items-center gap-2">
                              <EnvelopeIcon className="w-4 h-4 text-blue-500" />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{email}</span>
                            </div>
                            <button
                              onClick={() => setInvitedEmails(prev => prev.filter(e => e !== email))}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {invitedEmails.length === 0 && (
                      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                        Enter email addresses of people you'd like to invite.
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleSkipSetup}
                    disabled={isLoading}
                    className="px-5 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Skip for now
                  </button>
                  <button
                    onClick={handleFinish}
                    disabled={isLoading}
                    className="flex-1 flex items-center justify-center gap-2 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <CheckCircleIcon className="w-5 h-5" />
                    )}
                    {invitedEmails.length > 0 ? 'Send Invites & Finish' : 'Finish Setup'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer hint */}
          <p className="text-center text-xs text-gray-400 mt-6">
            You can always update these settings later from your dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
